import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useRef, useState } from 'react'
import { isIOS, isSafari } from 'react-device-detect'

import {
  CustomProfileConnectionFragmentDoc,
  GachaOrderDetailFragmentDoc,
  GachaPortfolioQuery,
  GachaPortfolioQueryVariables,
  GachaResultQuery,
  GachaResultQueryVariables,
  GachaRollType,
  GachaTicketConnectionFragmentDoc,
  GenerateWallpaperPrintQrcode,
  GenerateWallpaperPrintQrcodeMutation,
  GenerateWallpaperPrintQrcodeMutationVariables,
  PortfolioConnectionFragmentDoc,
  ProtectUserItem,
  ProtectUserItemMutation,
  ProtectUserItemMutationVariables,
  RollGacha,
  RollGachaMutation,
  RollGachaMutationVariables,
  UpdateCustomProfile,
  UpdateCustomProfileMutation,
  UpdateCustomProfileMutationVariables,
  UserGachaTicketConnectionFragmentDoc,
  UseTickets,
  UseTicketsMutation,
  UseTicketsMutationVariables,
} from '@/clients/utoniq-core/schema'
import {
  generateApng,
  playApngAnimation,
  VideoFullScreenRef,
} from '@/components/lib/animation'
import { isVideoExt } from '@/components/pages/GachaDetail/template/templateStore'
import { config } from '@/config'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { useMessage } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { captureError } from '@/core/errors'
import { Share } from '@/core/native'
import { getPlatform } from '@/core/native/app'
import { useDownlodImage } from '@/lib/downloadFile'
import { useCreateShortLink } from '@/lib/url/shortLink'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const { showError } = useMessage()
  const { analytics } = useAnalytics()
  const { router, generatePath, routerPages } = useRouter<{
    gachaId: string
    gachaOrderId: string
  }>()

  const [openSoldOutErrorModal, setOpenSoldOutErrorModal] = useState(false)

  /** ガチャを回している状態かどうか */
  const [gachaRolling, setGachaRolling] = useState(false)
  /** スタンバイ状態かどうか */
  const [standingBy, setStandingBy] = useState<boolean>(false)
  /** スタンバイ・本動画含めて動画を再生している時間かどうか */
  const [playingAnimation, setPlayingAnimation] = useState<boolean>(false)
  /** 動画スキップ処理 */
  const [handleSkip, setHandleSkip] = useState({ fn() {} })

  const lastGachaOrderIdRef = useRef<string | undefined>()

  // 動画再生コンポーネントのrefオブジェクトを作成する
  const videoFullscreenRef = useRef<VideoFullScreenRef>(null)
  const standbyVideoFullscreenRef = useRef<VideoFullScreenRef>(null)

  // APNG再生プレイヤーRef
  const apngFullScreenRef = useRef<HTMLCanvasElement>(null)
  const [apngAnimationPlaying, setApngAnimationPlaying] =
    useState<boolean>(false)

  const { loading, data } = useQuery<
    GachaResultQuery,
    GachaResultQueryVariables
  >(QUERY, {
    variables: {
      gachaOrderId: router.query.gachaOrderId,
      gachaTicketId: `organization:${config.organizationId}`,
    },
  })

  const [
    getGachaPortfolio,
    { data: selectedItem, loading: selectingItem, refetch },
  ] = useLazyQuery<GachaPortfolioQuery, GachaPortfolioQueryVariables>(
    PORTFOLIO,
    {
      returnPartialData: true,
    }
  )

  const [rollGacha] = useMutation<
    RollGachaMutation,
    RollGachaMutationVariables
  >(RollGacha)

  const [updateCustomProfile] = useMutation<
    UpdateCustomProfileMutation,
    UpdateCustomProfileMutationVariables
  >(UpdateCustomProfile)

  const [protectUserItem, { loading: protectingItem }] = useMutation<
    ProtectUserItemMutation,
    ProtectUserItemMutationVariables
  >(ProtectUserItem)

  const [onUseTickets] = useMutation<
    UseTicketsMutation,
    UseTicketsMutationVariables
  >(UseTickets)

  const [generateWallpaperPrintQrcode, { loading: QRCodePrinting }] =
    useMutation<
      GenerateWallpaperPrintQrcodeMutation,
      GenerateWallpaperPrintQrcodeMutationVariables
    >(GenerateWallpaperPrintQrcode)

  const redirectToGachaComplete = useCallback(
    (count: number, gachaResultData?: RollGachaMutation | null) => {
      standbyVideoFullscreenRef?.current?.pauseVideo()
      videoFullscreenRef?.current?.pauseVideo()

      if (
        lastGachaOrderIdRef.current ===
        gachaResultData?.rollGacha?.gachaOrder?.id
      ) {
        return
      }
      lastGachaOrderIdRef.current = gachaResultData?.rollGacha?.gachaOrder?.id

      // mutation・アニメーション正常終了後、ガチャ結果画面へ遷移
      return router.push(
        generatePath(
          count === 10
            ? routerPages.TenGachaResult
            : routerPages.SingleGachaResult,
          {
            gachaId: gachaResultData?.rollGacha?.gachaOrder?.gachaId,
            gachaOrderId: gachaResultData?.rollGacha?.gachaOrder?.id,
          }
        )
      )
    },
    [
      generatePath,
      router,
      routerPages.SingleGachaResult,
      routerPages.TenGachaResult,
    ]
  )

  const { downloadImage } = useDownlodImage()

  const getShortLinkUrl = useCreateShortLink({
    path: `/ownedItems/${selectedItem?.portfolio?.item.id}`,
  })

  return {
    state: {
      loading,
      user: data?.user ?? null,
      loggedUser: user,
      gachaRolling,
      standingBy,
      playingAnimation,
      apngAnimationPlaying,
      protectingItem,
      selectedItem: selectedItem?.portfolio ?? null,
      selectingItem,
      gachaOrder: data?.user?.gachaOrder ?? null,
      ticket: data?.user?.gachaTicket?.available ?? null,
      gachaTicket: data?.gachaTicket ?? null,
      soldOutModalInfo: {
        open: openSoldOutErrorModal,
        onClose: () => {
          setOpenSoldOutErrorModal(false)
        },
      },
      videoFullscreenRef,
      standbyVideoFullscreenRef,
      apngFullScreenRef,
      prepaidPointBalance: data?.prepaidPointBalance.balance ?? 0,
      formattedPrepaidPointBarance:
        data?.prepaidPointBalance.formattedPrepaidPointBarance ?? '0',
      isSafari: getPlatform() === 'ios' || isSafari || isIOS,
      QRCodePrinting,
    },
    actions: {
      /**
       * @description
       * ガチャを回す関数
       * ガチャを回すのに成功した場合：アニメーションの表示は成功・失敗に関わらずガチャ結果画面へと遷移させる。
       * ガチャを回すのに失敗した場合：スナックバーでエラー表示
       */
      async rollGacha(count: number, rollType: GachaRollType) {
        setGachaRolling(true)

        // ガチャを回すmutation
        return (
          rollGacha({
            variables: {
              rollGachaGachaId: router.query.gachaId,
              rollGachaInputs: {
                count,
                gachaTicketId: `organization:${config.organizationId}`,
                rollType,
              },
            },
          })
            // ガチャを回すmutation成功時
            .then(async ({ data: gachaResultData }) => {
              /** スキップ処理を「ガチャ結果先へのリダイレクト」に設定する */
              setHandleSkip({
                fn() {
                  setPlayingAnimation(false)
                  setStandingBy(false)
                  setApngAnimationPlaying(false)
                  setGachaRolling(false)
                  redirectToGachaComplete(count, gachaResultData)
                },
              })
              analytics?.track('click_rollGachaAgain', {
                applicationKey: config.applicationKey,
                gachaId: router.query.gachaId,
                artistId: data?.user?.gachaOrder?.gacha.artistId,
                /**
                 * @description
                 * カスタムディメンションは文字列しか計測しないため、数値を文字列変換する
                 * https://app.clickup.com/t/908127/UT-9829
                 */
                count: count.toString(),
                gachaTicketId: `organization:${config.organizationId}`,
                type: rollType,
              })

              // 現在のガチャの動画アニメーションのURL
              const rewardAnimationUri =
                gachaResultData?.rollGacha.gachaOrder.rewardAnimation?.uri

              /**
               * @description
               * ガチャのアニメーションを再生する処理
               * URLから動画ファイルがAPNGかMP4か判断し、ファイル種別ごとの処理を行う。
               * URLの取得に失敗、またはアニメーション再生に失敗した時はSentry通知だけして結果画面に遷移させる
               */
              if (!rewardAnimationUri) {
                return redirectToGachaComplete(count, gachaResultData)
              }

              /**
               * 動画ファイルがAPNGの場合
               * ミューテーション結果のファイルをAPNGプレイヤー要素で再生する
               */
              if (!isVideoExt(rewardAnimationUri)) {
                const animationApng = await generateApng(
                  rewardAnimationUri
                ).catch(e => {
                  captureError(e, 'gachaDetailGenerateApng')
                  return
                })
                if (animationApng) {
                  setApngAnimationPlaying(true)
                  await playApngAnimation(apngFullScreenRef, animationApng)
                    .then(() => {
                      setGachaRolling(false)
                    })
                    .catch(e => {
                      captureError(e, 'gachaDetailplayApngAnimation')
                    })
                    .finally(() => setApngAnimationPlaying(false))
                }
                return redirectToGachaComplete(count, gachaResultData)
              }

              /**
               * 動画ファイルがMP4の場合
               * 結果アニメーション動画のvideo要素にURLをセットする
               */
              if (!videoFullscreenRef || !videoFullscreenRef.current) {
                return redirectToGachaComplete(count, gachaResultData)
              }
              videoFullscreenRef?.current?.init((state: any) => ({
                ...state,
                url: rewardAnimationUri,
                playsinline: true,
                onReady() {
                  videoFullscreenRef.current?.handleOnReadyVideo()
                },
                onEnded() {
                  setPlayingAnimation(false)
                  setGachaRolling(false)
                  redirectToGachaComplete(count, gachaResultData)
                },
                onError(e: Error) {
                  setPlayingAnimation(false)
                  captureError(e, 'gachaDetailPlayGachaAnimationVideo')
                  redirectToGachaComplete(count, gachaResultData)
                },
              }))

              setPlayingAnimation(true)
              /**
               * スタンバイ動画がある場合、スタンバイ動画を再生してreturn
               */
              if (data?.user?.gachaOrder?.gacha.standByAnimations) {
                standbyVideoFullscreenRef?.current?.playVideo()
                return
              }
              /**
               * スタンバイ動画がない場合、そのままアニメーションを再生
               */
              videoFullscreenRef?.current?.playVideo()
            })
            .catch(e => {
              // ガチャを回したタイミングで売り切れてしまった場合固有のモーダル表示
              if (e?.message === 'There is no item set available.') {
                setOpenSoldOutErrorModal(true)
              }

              showError(e)
            })
            .finally(() => {
              setGachaRolling(false)
            })
        )
      },
      rollGachaAfterStandingby() {
        setStandingBy(false)
        // アニメーション動画を再生
        videoFullscreenRef?.current?.playVideo()
      },
      async onSelectedItem(itemId) {
        await getGachaPortfolio({ variables: { id: itemId } })
      },
      onClickShare: async portfolio => {
        const url = await getShortLinkUrl()
        await Share.share({
          title: portfolio?.item.title,
          text:
            portfolio?.item.description ??
            portfolio?.item.metadata?.ticket?.description ??
            t('metaDescription'),
          url,
          dialogTitle: portfolio?.item.title,
        }).catch(e => showError(e))

        return
      },
      onSettingWallpaper: async (itemId?: string) => {
        const unsetWallpaper =
          data?.user?.customProfile?.wallpaperItem?.id === itemId
        await updateCustomProfile({
          variables: {
            updateCustomProfileInputs: {
              wallpaperItemId: unsetWallpaper ? null : itemId,
            },
          },
        })
        return unsetWallpaper
      },
      async onProtectUserItem(values) {
        await protectUserItem({ variables: { inputs: values } })
      },
      async onImageDownload(url: string | undefined) {
        if (isEmpty(url)) return
        url && (await downloadImage(url))
      },
      // スタンバイ動画URLをvideo要素にセットする処理
      onInitializeStandbyAnimation(count: number) {
        const standByAnimations =
          data?.user?.gachaOrder?.gacha.standByAnimations

        if (!standByAnimations) return

        standbyVideoFullscreenRef?.current?.init((state: any) => ({
          ...state,
          url: standByAnimations[count === 1 ? 'single' : 'bulk']?.uri,
          playsinline: true,
          onReady() {
            standbyVideoFullscreenRef.current?.handleOnReadyVideo()
          },
          onEnded() {
            setStandingBy(true)
          },
          onError(e: Error) {
            captureError(e, 'gachaDetailPlayGachaAnimationVideo')
            //check videoFullscreen have src value
            if (videoFullscreenRef?.current?.src) {
              videoFullscreenRef?.current?.playVideo()
            }
          },
        }))
      },
      // 動画のスキップ処理
      onSkip: handleSkip.fn,
      async onUseTicket(itemId, serialIds, throughTicketName) {
        if (!itemId) return
        await onUseTickets({
          variables: {
            inputs: {
              items: [{ itemId, serialIds, throughTicketName }],
            },
          },
        }).catch(e => {
          showError(e)

          /**
           * @note
           * アイテム使用時にエラーが起きた場合
           * SwipeButtonコンポーネントでエラーハンドリングが呼ばれる
           */
          throw e
        })
      },
      async onSettingAvatar(avatarItemId) {
        if (!avatarItemId) return
        await updateCustomProfile({
          variables: {
            updateCustomProfileInputs: { avatarItemId },
          },
        })
      },
      async onGenerateWallpaperPrintQrcode(serialId: string) {
        await generateWallpaperPrintQrcode({
          variables: {
            inputs: {
              itemId: selectedItem?.portfolio?.item.id ?? '',
              serialId,
            },
          },
        }).then(() => refetch())
      },
    },
  }
})

const QUERY = gql`
  query GachaResult($gachaOrderId: ID!, $gachaTicketId: ID!) {
    user {
      id
      gachaOrder(id: $gachaOrderId) {
        ...GachaOrderDetail
      }
      gachaTicket(id: $gachaTicketId) {
        ...UserGachaTicketConnection
      }
      customProfile {
        ...CustomProfileConnection
      }
    }
    gachaTicket(id: $gachaTicketId) {
      ...GachaTicketConnection
    }
    prepaidPointBalance {
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
  }
  ${GachaOrderDetailFragmentDoc}
  ${GachaTicketConnectionFragmentDoc}
  ${UserGachaTicketConnectionFragmentDoc}
  ${CustomProfileConnectionFragmentDoc}
`

const PORTFOLIO = gql`
  query GachaPortfolio($id: ID!, $skuId: String) {
    portfolio(id: $id) {
      ...PortfolioConnection
    }
  }
  ${PortfolioConnectionFragmentDoc}
`
